/**
 * Atlas PM · 权限配置 · 角色权限矩阵（P4.4）
 *
 * 布局：左侧角色列表（7 个）+ 右侧按 category 分组的权限点勾选矩阵。
 *
 * 关键交互：
 *   - 切换角色时若存在未保存改动 → 显示「放弃并切换」确认条
 *   - SUPER_ADMIN（isBuiltin && code==='SUPER_ADMIN'）→ 全部勾选框置灰
 *   - 每组提供「全选 / 全不选」
 *   - 保存走 PUT /system/roles/:id/permissions，成功后失效并刷新角色查询
 *   - 失败直接展示后端返回的中文 message（含错误码前缀）
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import Badge from '../../../components/Badge';
import Button from '../../../components/Button';
import Spinner from '../../../components/Spinner';
import { ApiError } from '../../../api/client';
import {
  ROLE_ERROR_LABEL,
  isProtectedRole,
  type PermissionItem,
  type RoleItem,
} from './api';
import { usePermissions, useRoles, useUpdateRolePermissions } from './useRoles';
import './roles.css';

/* ── 权限点中文标签（目录表无 name 列，在此做展示映射） ── */
const PERM_LABELS: Record<string, string> = {
  'task:create': '新建任务',
  'task:delete': '删除任务',
  'task:submit': '提交任务',
  'task:update': '编辑任务',
  'task:view': '查看任务',
  // ⭐ P6.38：指标与要素（此前后端接口已要求，但前端矩阵缺标签）
  'metric:manage': '指标与要素管理',
  'element:fill': '填报要素值',
  'freeze:unlock_request': '申请解冻',
  'freeze:unlocked_edit': '解冻窗口内编辑',
  'approval:addsign': '加签',
  'approval:approve': '同意',
  'approval:proxy': '代审批',
  'approval:reject': '驳回',
  'approval:transfer': '转交',
  'approval:view': '查看审批',
  'scope:all_provinces': '全国数据范围',
  'scope:own_province': '本省数据范围',
  'audit:view': '查看操作日志',
  'dict:manage': '元数据 / 字典管理',
  'file:download': '下载附件',
  'metadata:design': '任务形态设计',
  'role:manage': '角色权限管理',
  'user:manage': '用户管理',
  'project:create': '新建项目',
  'project:delete': '删除项目',
  'project:update': '编辑项目',
  'project:view': '查看项目',
};

function permLabel(item: PermissionItem): string {
  return PERM_LABELS[item.code] ?? item.code;
}

/** 排序后的 code 串，用于稳定比较集合是否相等 */
function sig(codes: readonly string[]): string {
  return [...codes].sort((a, b) => a.localeCompare(b)).join(',');
}

/** 把异常转成用户可读的中文文案（优先后端 message） */
function toFriendlyMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const prefix = ROLE_ERROR_LABEL[err.code];
    const msg = err.message || '保存失败';
    return prefix && !msg.startsWith(prefix) ? `${prefix}：${msg}` : msg;
  }
  if (err instanceof Error) return err.message;
  return '保存失败，请稍后重试';
}

interface ToastState {
  tone: 'ok' | 'err';
  text: string;
}

export default function RolesMatrix() {
  const {
    data: rolesData,
    isLoading: rolesLoading,
    isError: rolesIsError,
    error: rolesError,
  } = useRoles();
  const {
    data: permData,
    isLoading: permLoading,
    isError: permIsError,
    error: permError,
  } = usePermissions();
  const saveMut = useUpdateRolePermissions();

  const roles = useMemo<RoleItem[]>(() => rolesData?.items ?? [], [rolesData]);
  const groups = useMemo(() => permData?.groups ?? [], [permData]);
  const totalPerms = useMemo(
    () => permData?.total ?? groups.reduce((n, g) => n + g.permissions.length, 0),
    [permData, groups],
  );

  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  /* 首次加载后默认选中第一个角色 */
  useEffect(() => {
    const first = roles[0];
    if (!selectedRoleId && first) setSelectedRoleId(first.id);
  }, [roles, selectedRoleId]);

  const role = useMemo(
    () => roles.find((r) => r.id === selectedRoleId) ?? null,
    [roles, selectedRoleId],
  );
  const roleProtected = role ? isProtectedRole(role) : false;
  const rolePermSig = role ? sig(role.permissions) : '';

  /* 角色切换或服务端数据刷新后，重置草稿 */
  useEffect(() => {
    if (role) setDraft([...role.permissions]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoleId, rolePermSig]);

  const dirty = Boolean(role) && sig(draft) !== rolePermSig;

  /* toast 自动消失 */
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const showToast = useCallback((tone: ToastState['tone'], text: string) => {
    setToast({ tone, text });
  }, []);

  const selectRole = useCallback(
    (id: string) => {
      if (id === selectedRoleId) return;
      if (dirty) {
        setPendingSwitch(id);
        return;
      }
      setPendingSwitch(null);
      setSelectedRoleId(id);
    },
    [dirty, selectedRoleId],
  );

  const confirmDiscard = useCallback(() => {
    if (!pendingSwitch) return;
    setSelectedRoleId(pendingSwitch);
    setPendingSwitch(null);
    showToast('ok', '已放弃未保存的改动');
  }, [pendingSwitch, showToast]);

  const toggle = useCallback(
    (code: string) => {
      if (roleProtected) return;
      setDraft((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
      );
    },
    [roleProtected],
  );

  const setGroup = useCallback(
    (items: PermissionItem[], on: boolean) => {
      if (roleProtected) return;
      const codes = items.map((p) => p.code);
      setDraft((prev) => {
        const s = new Set(prev);
        for (const c of codes) {
          if (on) s.add(c);
          else s.delete(c);
        }
        return [...s];
      });
    },
    [roleProtected],
  );

  const onSave = useCallback(() => {
    if (!role || roleProtected || !dirty) return;
    saveMut.mutate(
      { roleId: role.id, permissionCodes: [...draft].sort((a, b) => a.localeCompare(b)) },
      {
        onSuccess: (res) =>
          showToast('ok', `「${role.name}」权限已保存（共 ${res.permissions.length} 项）`),
        onError: (err) => showToast('err', toFriendlyMessage(err)),
      },
    );
  }, [draft, dirty, role, roleProtected, saveMut, showToast]);

  if (rolesLoading || permLoading) {
    return <Spinner label="加载角色与权限点…" />;
  }
  if (rolesIsError || permIsError) {
    return (
      <div className="roles__error" data-testid="roles-error">
        <div className="roles__error-title">权限配置加载失败</div>
        <div className="roles__error-msg">
          {toFriendlyMessage(rolesError ?? permError)}
        </div>
        <Button variant="secondary" onClick={() => window.location.reload()}>
          重新加载
        </Button>
      </div>
    );
  }
  if (roles.length === 0) {
    return (
      <div className="roles__error" data-testid="roles-empty">
        <div className="roles__error-title">暂无可配置的角色</div>
      </div>
    );
  }

  const selectedCodes = new Set(draft);

  return (
    <div className="roles" data-testid="roles-matrix">
      {/* ── 左：角色列表 ── */}
      <aside className="roles__side" aria-label="角色列表">
        <div className="roles__side-label">角色（{roles.length}）</div>
        {roles.map((r) => {
          const on = r.id === selectedRoleId;
          const protectedRole = isProtectedRole(r);
          return (
            <button
              key={r.id}
              type="button"
              className={['roles__role', on ? 'on' : ''].filter(Boolean).join(' ')}
              data-role-code={r.code}
              aria-pressed={on}
              onClick={() => selectRole(r.id)}
            >
              <span className="roles__role-main">
                <span className="roles__role-name">{r.name}</span>
                <span className="roles__role-code">{r.code}</span>
              </span>
              <span className="roles__role-meta">
                {protectedRole && <Badge tone="frozen">受保护</Badge>}
                <span className="roles__role-count">{r.permissions.length}</span>
              </span>
            </button>
          );
        })}
      </aside>

      {/* ── 右：权限矩阵 ── */}
      <section className="roles__main">
        {role && (
          <>
            <header className="roles__head">
              <div className="roles__head-info">
                <span className="roles__head-name">{role.name}</span>
                <span className="roles__head-code">{role.code}</span>
                {dirty && <Badge tone="warn">未保存</Badge>}
              </div>
              <div className="roles__head-actions">
                <span className="roles__head-total" data-testid="roles-total">
                  共 {totalPerms} 个权限点
                </span>
                <Button
                  variant="primary"
                  disabled={!dirty || roleProtected || saveMut.isPending}
                  onClick={onSave}
                  data-testid="roles-save"
                >
                  {saveMut.isPending ? '保存中…' : '保存'}
                </Button>
              </div>
            </header>

            {roleProtected && (
              <div className="roles__notice" data-testid="roles-protected-notice">
                <span className="roles__notice-icon" aria-hidden="true">
                  ⚠
                </span>
                超级管理员的权限不可修改，防止把系统锁死
              </div>
            )}

            {pendingSwitch && (
              <div className="roles__discard" data-testid="roles-unsaved">
                <span className="roles__discard-text">
                  「{role.name}」有未保存的改动，切换角色将丢弃这些改动。
                </span>
                <div className="roles__discard-actions">
                  <Button variant="danger" size="sm" onClick={confirmDiscard}>
                    放弃并切换
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPendingSwitch(null)}
                  >
                    取消
                  </Button>
                </div>
              </div>
            )}

            <div className="roles__groups">
              {groups.map((group) => {
                const codes = group.permissions.map((p) => p.code);
                const selectedCount = codes.filter((c) => selectedCodes.has(c)).length;
                const allOn = selectedCount === codes.length && codes.length > 0;
                const someOn = selectedCount > 0 && !allOn;
                return (
                  <div className="roles__group" key={group.category} data-category={group.category}>
                    <div className="roles__group-head">
                      <span className="roles__group-title">{group.category}</span>
                      <span className="roles__group-count">
                        {selectedCount}/{codes.length}
                      </span>
                      <button
                        type="button"
                        className="roles__group-btn"
                        disabled={roleProtected}
                        data-group-toggle={group.category}
                        onClick={() => setGroup(group.permissions, !allOn)}
                      >
                        {allOn ? '全不选' : '全选'}
                      </button>
                      {someOn && <span className="roles__group-partial">部分选中</span>}
                    </div>
                    <div className="roles__perms">
                      {group.permissions.map((p) => (
                        <label
                          key={p.code}
                          className={[
                            'roles__perm',
                            selectedCodes.has(p.code) ? 'on' : '',
                            roleProtected ? 'locked' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          title={p.code}
                        >
                          <input
                            type="checkbox"
                            data-perm={p.code}
                            checked={selectedCodes.has(p.code)}
                            disabled={roleProtected}
                            onChange={() => toggle(p.code)}
                          />
                          <span className="roles__perm-label">{permLabel(p)}</span>
                          <span className="roles__perm-code">{p.code}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      {toast && (
        <div
          className={['roles__toast', `roles__toast--${toast.tone}`].join(' ')}
          data-testid="roles-toast"
          role="status"
        >
          <span className="roles__toast-text">{toast.text}</span>
          <button
            type="button"
            className="roles__toast-close"
            aria-label="关闭提示"
            onClick={() => setToast(null)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
